import Config from 'react-native-config'
import { Env, isProductionEnvironment } from './apollo'

const STRIPE_SCRIPT_ID = 'stripe-js'
const STRIPE_SCRIPT_URL = 'https://js.stripe.com/v3/'

export interface StripeCardElement {
  destroy: () => void
  focus: () => void
  mount: (domElement: unknown) => void
  on: (event: 'change' | 'ready', handler: (event?: any) => void) => void
  unmount: () => void
}

export interface StripeElements {
  create: (type: 'card', options?: Record<string, unknown>) => StripeCardElement
}

export interface StripePaymentMethod {
  id: string
  card?: {
    brand: string
    exp_month: number
    exp_year: number
    last4: string
  }
}

export interface Stripe {
  confirmCardSetup: (
    clientSecret: string,
    data: { payment_method: string },
  ) => Promise<{
    error?: Error
    setupIntent?: {
      payment_method?: string | StripePaymentMethod
    }
  }>
  createPaymentMethod: (data: {
    type: 'card'
    card: StripeCardElement
    billing_details?: {
      email?: string | null
      name?: string | null
      phone?: string | null
    }
  }) => Promise<{ error?: Error; paymentMethod?: StripePaymentMethod }>
  elements: () => StripeElements
}

const getPublishableKeyByEndpointEnv = (endpoint: Env) => {
  if (isProductionEnvironment(endpoint)) {
    return Config.PRODUCTION_STRIPE_PUBLISHABLE_KEY
  }

  return Config.TEST_STRIPE_PUBLISHABLE_KEY
}

let publishableKey: string | undefined
let stripePromise: Promise<Stripe> | undefined

const loadStripeScript = () => {
  const webWindow = globalThis as any
  const webDocument = webWindow.document

  if (webWindow.Stripe) {
    return Promise.resolve()
  }

  const existingScript = webDocument.getElementById(STRIPE_SCRIPT_ID)

  if (existingScript) {
    return new Promise<void>((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve())
      existingScript.addEventListener('error', () => reject(new Error('Unable to load Stripe.js')))
    })
  }

  return new Promise<void>((resolve, reject) => {
    const script = webDocument.createElement('script')
    script.id = STRIPE_SCRIPT_ID
    script.src = STRIPE_SCRIPT_URL
    script.async = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => reject(new Error('Unable to load Stripe.js')))
    webDocument.head.appendChild(script)
  })
}

const buildStripe = async () => {
  if (!publishableKey) {
    throw new Error('Stripe publishable key is missing')
  }

  await loadStripeScript()

  const webWindow = globalThis as any

  if (!webWindow.Stripe) {
    throw new Error('Stripe.js is unavailable')
  }

  return webWindow.Stripe(publishableKey)
}

const configure = (endpointEnv: Env) => {
  const nextPublishableKey = getPublishableKeyByEndpointEnv(endpointEnv)

  if (publishableKey === nextPublishableKey && stripePromise) {
    return stripePromise
  }

  publishableKey = nextPublishableKey
  stripePromise = buildStripe()

  return stripePromise
}

const getStripe = () => {
  if (!stripePromise) {
    stripePromise = buildStripe()
  }

  return stripePromise
}

export default { configure, getStripe }
